import { useLoading, UseLoadingActions } from '@apparel/react/hooks/useLoading';
import { useService } from '@apparel/react/hooks/useService';
import { AxiosRequestConfig } from 'axios';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { CartEmitter, CartEvents } from './CartEmitter';
import { CartItem, CartPrices } from './CartResult';
import { CartService } from './CartService';
import { PackageList } from './PackageList';
import { SkuList } from './SkuList';

export interface UseCartItems {
    /**
     * Boolean value showing if the cart it's loading or not.
     */
    loading: boolean;

    /**
     * Storing error that happen during loading items.
     */
    error: Error | null;

    /**
     * Store raw items coming from database
     */
    items: CartItem[] | null;

    /**
     * Store the prices preview result
     */
    prices: CartPrices | null;

    /**
     * Grouped skus
     */
    skus: SkuList;

    /**
     * Grouped packages
     */
    packages: PackageList;

    /**
     * Number of the items
     */
    count: number;

    /**
     * Return if the cart has/hasn't items.
     */
    hasItems: boolean;

    /**
     * Return if the cart has/hasn't prices.
     */
    hasPrices: boolean;

    /**
     * Loading actions useful to trigger loading state.
     */
    loadingActions: UseLoadingActions;

    /**
     * Reload items
     */
    reload(): void;
}

export const useCartItems = (config?: AxiosRequestConfig): UseCartItems => {
    const cart$ = useService(CartService);

    const [loading, loadingActions] = useLoading(true);
    const [error, setError] = useState<Error | null>(null);
    const [items, setItems] = useState<CartItem[] | null>(null);
    const [prices, setPrices] = useState<CartPrices | null>(null);

    const hasItems = useMemo(() => (items?.length ?? 0) > 0, [items]);
    const hasPrices = useMemo(() => !!prices, [prices]);

    const handleLoadItems = useMemo(
        () =>
            debounce(async () => {
                loadingActions.start();
                try {
                    const data = await cart$.loadCart(config);
                    setItems(
                        () =>
                            data?.items.map((item) => {
                                if (item?.package?.subcategories) {
                                    item.package.subcategories = item.package.subcategories.map(
                                        ({ default_sku, default_sku_id, subcategory_to_repeat, subcategory }) => ({
                                            ...subcategory,
                                            default_sku,
                                            default_sku_id,
                                            subcategory_to_repeat,
                                        }),
                                    );
                                }

                                return item;
                            }) || [],
                    );
                    setPrices(() => data?.prices || ({} as any));
                    setError(() => null);
                } catch (e) {
                    setError(() => e);
                }

                loadingActions.stop();
            }, 300),
        [setItems, setPrices, setError, config],
    );

    const skus = useMemo(() => SkuList.fromItems(items ?? []), [items]);
    const packages = useMemo(() => PackageList.fromItems(items ?? []), [items]);
    const count = useMemo(() => skus.count() + packages.count(), [skus, packages]);

    useEffect(() => {
        const offAdd = CartEmitter.on(CartEvents.ADD, handleLoadItems);
        const offUpdate = CartEmitter.on(CartEvents.UPDATE, handleLoadItems);
        const offRemove = CartEmitter.on(CartEvents.REMOVE, handleLoadItems);
        const offClear = CartEmitter.on(CartEvents.CLEAR, handleLoadItems);

        // noinspection JSIgnoredPromiseFromCall
        handleLoadItems();

        return () => {
            offAdd();
            offUpdate();
            offRemove();
            offClear();
        };
    }, []);

    return {
        loading,
        error,
        items,
        prices,
        skus,
        packages,
        count,
        hasItems,
        hasPrices,
        loadingActions,

        reload: handleLoadItems,
    };
};
